import IApplicationForm from "@/module/main/model/applicationForm/IApplicationForm";
import IEnvSettings from "@/module/main/model/applicationForm/IEnvSettings";
import ApplicationFactory from "@/module/main/model/applicationForm/ApplicationFactory";
import FinancedSubjectType from "@/module/main/dto/FinancedSubjectTypeEnum";
import ClientType from "@/module/main/dto/ClientTypeEnum";
import OrderType from "@/module/main/dto/OrderTypeEnum";
import FinancingType from "@/module/main/dto/FinancingTypeEnum";
import CashFinancingType from "@/module/main/dto/CashFinancingTypeEnum";
import FinancingApiVersion from "@/module/main/dto/FinancingApiVersionEnum";
import CarLoanType from "@/module/main/dto/CarLoanTypeEnum";
import Culture from "@/module/main/page/CultureEnum";
import YesNo from "@/app/service/YesNoEnum";
import TemplateOptions from "@/module/main/page/TemplateOptionsEnum";
import EnvType from "@/module/main/page/EnvTypeEnum";
import Brand from "@/module/main/model/applicationForm/BrandEnum";
import Brands from "@/module/main/model/Brands";

export type LocalStorageGetters = Record<string, any>;

// TEST env is lowercase (cz10d2), PREPROD and PROD env is the enum key (CZ01P, CZ)
const ENV_COUNTRY_PREFIX = /^(cz|sk)/i;

function getTemplate(culture: Culture): TemplateOptions
{
    return culture === Culture.SK ? TemplateOptions.SK : TemplateOptions.CZ;
}

function getEnvForCulture(env: string, culture: Culture): string
{
    const country = getTemplate(culture).toLowerCase();

    return env.replace(ENV_COUNTRY_PREFIX, prefix => prefix === prefix.toUpperCase() ? country.toUpperCase() : country);
}

function applyStoredEnvSettings(envSettings: IEnvSettings, culture: Culture, getters: LocalStorageGetters): void
{
    const envType: string | undefined = getters["main/getEnvType"];
    !!envType && (envSettings.envType = envType as EnvType);

    const env: string | undefined = getters["main/getEnv"];
    !!env && (envSettings.env = getEnvForCulture(env, culture));

    const showQrCode: boolean | undefined = getters["main/getShowQrCode"];
    !!showQrCode && (envSettings.showQrCode = showQrCode);

    const copyToClipboard: boolean | undefined = getters["main/getCopyToClipboard"];
    !!copyToClipboard && (envSettings.copyToClipboard = copyToClipboard);

    const financingApiVersion: string | undefined = getters["main/getFinancingApiVersion"];
    !!financingApiVersion && (envSettings.financingApiVersion = financingApiVersion as FinancingApiVersion);
}

export function getStoredFinancedSubjectType(getters: LocalStorageGetters): FinancedSubjectType
{
    return getters["main/getFinancedSubjectType"] as FinancedSubjectType || FinancedSubjectType.CASH;
}

export function applyStoredSelection(form: IApplicationForm, financedSubjectType: FinancedSubjectType,
    getters: LocalStorageGetters): void
{
    const storedClientType = getters["main/getClientType"] || ClientType.PERSON;
    const orderType = getters["main/getOrderType"] || OrderType.STANDARD;
    const financingApiVersion = getters["main/getFinancingApiVersion"] || FinancingApiVersion.V1;
    // financing/v1 supports only PERSON
    const clientType = financedSubjectType === FinancedSubjectType.ORDER && orderType === OrderType.STANDARD
        && financingApiVersion === FinancingApiVersion.V1
        ? ClientType.PERSON
        : storedClientType;
    const financingType = getters["main/getFinancingType"] || FinancingType.INSTALLMENT;
    const cashFinancingType = getters["main/getCashFinancingType"] || CashFinancingType.LOAN_WALLET;
    const walletWithOrder = getters["main/getWalletWithOrder"] || YesNo.NO;
    // large cars are supported only in CZ
    const carLoanType = form.culture === Culture.CZ
        ? getters["main/getCarLoanType"] || CarLoanType.SMALL_CARS
        : CarLoanType.SMALL_CARS;

    form.envSettings.financedSubjectType = financedSubjectType;
    form.envSettings.orderType = orderType;
    form.envSettings.carLoanType = carLoanType;

    form.loan = ApplicationFactory.getLoan(form.culture, financedSubjectType, clientType, orderType,
        financingType, cashFinancingType, walletWithOrder, carLoanType);
    form.customer = ApplicationFactory.getCustomer(form.culture, financedSubjectType, orderType,
        form.loan.clientType, carLoanType);
}

function applyStoredBrand(envSettings: IEnvSettings, culture: Culture, getters: LocalStorageGetters): void
{
    const template = getTemplate(culture);
    const storedBrand: Brand | undefined = getters["main/getEnvBrand"];
    const allowedBrands = Brands.getBrands(envSettings.financedSubjectType, envSettings.orderType, envSettings.carLoanType);
    const brand = !!storedBrand && allowedBrands.includes(storedBrand) && !!Brands.getCredential(storedBrand, template)
        ? storedBrand
        : Brands.getBrand(envSettings.financedSubjectType, envSettings.orderType, envSettings.carLoanType);

    envSettings.brand = brand;
    const credential = Brands.getCredential(brand, template);
    if (!!credential) {
        envSettings.username = credential.username;
        envSettings.password = credential.password;
    }
}

export function getTemplateWithStoredSettings(culture: Culture, getters: LocalStorageGetters): IApplicationForm
{
    const form = ApplicationFactory.getTemplate(culture);
    applyStoredEnvSettings(form.envSettings, culture, getters);
    applyStoredSelection(form, getStoredFinancedSubjectType(getters), getters);
    applyStoredBrand(form.envSettings, culture, getters);
    form.endpoints = ApplicationFactory.getEndpoints(form.envSettings.envType, form.envSettings.env);

    return form;
}

export function getTemplateForStoredEnv(getters: LocalStorageGetters): TemplateOptions
{
    const env: string = getters["main/getEnv"] || "";

    return env.substring(0, 2).toLowerCase() === "sk" ? TemplateOptions.SK : TemplateOptions.CZ;
}
